import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { Code, GlassIconButton } from 'dashboard/components/common';

/**
 * Add or edit a subdomain routing rule.
 *
 *   subdomain → "" (apex) | "shop" | "blog" | …
 *   target    → 'webapp'  → serve the full storefront SPA
 *             | 'page'    → serve a single page from the page builder
 *             | 'dashboard' → serve this brand's own admin, white-labelled
 *             | 'coming_soon' → render the placeholder page
 *
 * The page selector lazily loads `listSitePages` so the dialog stays
 * cheap to open when the owner just wants to add a webapp / coming-soon
 * rule.
 */
export default function SubdomainRuleDialog({
  open,
  onClose,
  brandId,
  apex,
  editing,
  onSaved,
}) {
  const [subdomain, setSubdomain] = useState('');
  const [targetType, setTargetType] = useState('coming_soon');
  const [pageId, setPageId] = useState('');
  const [note, setNote] = useState('');
  const [pages, setPages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset form when the dialog is (re-)opened.
  useEffect(() => {
    if (!open) return;
    setSubdomain(editing?.subdomain ?? '');
    setTargetType(editing?.target_type || 'coming_soon');
    setPageId(editing?.target_page_id || '');
    setNote(editing?.note || '');
  }, [open, editing]);

  // Pull the page list only when "Specific page" is selected, so the
  // call doesn't fire for every dialog open.
  useEffect(() => {
    if (!open || targetType !== 'page' || !brandId) return;
    if (pages.length > 0) return;
    setPagesLoading(true);
    API.listSitePages({ brand_id: brandId })
      .then((res) => setPages(Array.isArray(res) ? res : []))
      .catch((err) => toast.error(err?.message || 'Failed to load pages', { toastId: 'sub-pages-load' }))
      .finally(() => setPagesLoading(false));
  }, [open, targetType, brandId, pages.length]);

  const save = () => {
    if (targetType === 'page' && !pageId) {
      toast.error('Pick a page first');
      return;
    }
    setSaving(true);
    const call = editing
      ? API.updateBrandSubdomain({
          brand_id: brandId,
          _id: editing._id,
          target_type: targetType,
          target_page_id: targetType === 'page' ? pageId : null,
          note,
        })
      : API.addBrandSubdomain({
          brand_id: brandId,
          subdomain,
          target_type: targetType,
          target_page_id: targetType === 'page' ? pageId : null,
          note,
        });
    call
      .then(() => { toast.success('Saved'); onSaved?.(); onClose(); })
      .catch((err) => toast.error(err?.message || 'Save failed'))
      .finally(() => setSaving(false));
  };

  const fullHost = subdomain ? `${subdomain}.${apex || '<your-domain>'}` : (apex || '<your-domain>');

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth scroll='paper'>
      <DialogTitle sx={{ pr: '48px' }}>
        {editing ? `Edit subdomain rule` : 'Add subdomain rule'}
        <GlassIconButton label='Close'
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size='small'
        >
          <CloseIcon fontSize='small' />
        </GlassIconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing='14px'>
          <Box>
            <TextField
              label='Subdomain'
              size='small'
              value={subdomain}
              onChange={(e) => setSubdomain(e.target.value.toLowerCase())}
              placeholder='shop'
              fullWidth
              disabled={Boolean(editing)}
              helperText='Single label only — no dots. Leave blank to map the bare domain (apex).'
            />
            <Typography variant='caption' color='text.secondary' display='block' mt='6px'>
              Full hostname: <Code>{fullHost}</Code>
            </Typography>
          </Box>

          <Box>
            <Typography variant='body2' fontWeight='600' mb='6px'>What to serve</Typography>
            <RadioGroup
              value={targetType}
              onChange={(e) => setTargetType(e.target.value)}
            >
              <FormControlLabel
                value='webapp'
                control={<Radio size='small' />}
                label={
                  <Box>
                    <Typography variant='body2' fontWeight='600'>Full storefront</Typography>
                    <Typography variant='caption' color='text.secondary'>
                      Serve the whole shop, cart, checkout — anything under this subdomain renders the brand's storefront SPA.
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value='page'
                control={<Radio size='small' />}
                label={
                  <Box>
                    <Typography variant='body2' fontWeight='600'>Specific page from the page builder</Typography>
                    <Typography variant='caption' color='text.secondary'>
                      One page only — pick which one below.
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value='dashboard'
                control={<Radio size='small' />}
                label={
                  <Box>
                    <Typography variant='body2' fontWeight='600'>Your dashboard</Typography>
                    <Typography variant='caption' color='text.secondary'>
                      Where you manage the business, on an address of your own —
                      admin.yourdomain.com rather than a path on the site your
                      customers are looking at. It shows only this brand, with
                      none of our branding.
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value='coming_soon'
                control={<Radio size='small' />}
                label={
                  <Box>
                    <Typography variant='body2' fontWeight='600'>Coming soon (placeholder)</Typography>
                    <Typography variant='caption' color='text.secondary'>
                      Default. Shows a polite "we're working on it" page until you decide what should live here.
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </Box>

          {targetType === 'page' && (
            <FormControl size='small' fullWidth>
              <InputLabel shrink>Page</InputLabel>
              <Select
                displayEmpty
                label='Page'
                value={pageId}
                onChange={(e) => setPageId(e.target.value)}
                disabled={pagesLoading}
              >
                {pagesLoading && <MenuItem value=''>Loading…</MenuItem>}
                {!pagesLoading && pages.length === 0 && (
                  <MenuItem value=''>No pages yet — create one in Web page manager first.</MenuItem>
                )}
                {pages.map((pg) => (
                  <MenuItem key={pg._id} value={pg._id}>
                    {pg.title || pg.slug} {pg.slug && `(/${pg.slug})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            label='Note (optional)'
            size='small'
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Why this routing? (internal only)"
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: '14px 20px' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save rule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
